import type { BillingPlan, TimePeriod } from '../types';
import { generateSelectPlanForSignUpUrl, isFreePlan } from './helpers';

export interface PlanCtaContext {
  timePeriod: TimePeriod;
  hasValidCoupon?: boolean;
  canDirectPurchase: boolean;
  isHelloSign: boolean;
  isLoggedIn?: boolean;
  hasPaidCurrentPlan?: boolean;
  isFreeTrialEligible?: boolean;
  canChangePlan?: boolean;
}

export function isCurrentPlan(plan: BillingPlan) {
  return Boolean(
    plan.is_current_tier ||
      (plan.current_plan && plan.current_plan === plan.plan_id),
  );
}

export function usesDirectPurchaseOnlyVariant(
  plan: BillingPlan,
  context: PlanCtaContext,
) {
  return Boolean(
    context.canDirectPurchase && !context.isHelloSign && !isFreePlan(plan),
  );
}

export function buildPlanCtaUrl(
  plan: BillingPlan,
  context: PlanCtaContext,
  options: { directPurchase?: boolean } = {},
) {
  return buildPlanSignupUrl(plan, context, options);
}

function getPricingSource(plan: BillingPlan, context: PlanCtaContext) {
  if (plan.is_api) {
    return 'api-pricing';
  }

  if (context.isHelloSign) {
    return 'hs-pricing';
  }

  return 'hf-pricing';
}

export function buildPlanSignupUrl(
  plan: BillingPlan,
  context: PlanCtaContext,
  options: { directPurchase?: boolean } = {},
) {
  const couponCode = context.hasValidCoupon
    ? plan.coupon_code ?? undefined
    : undefined;

  return generateSelectPlanForSignUpUrl({
    planId: plan.plan_id,
    timePeriod: context.timePeriod,
    couponCode,
    isDirectPurchase: options.directPurchase,
    source: getPricingSource(plan, context),
  });
}

export function buildPlanChangeUrl(plan: BillingPlan, context: PlanCtaContext) {
  return buildPlanSignupUrl(plan, context);
}

export function shouldShowPlanDirectPurchaseLink(
  plan: BillingPlan,
  context: PlanCtaContext,
) {
  return (
    !isFreePlan(plan) &&
    !usesDirectPurchaseOnlyVariant(plan, context) &&
    !context.isLoggedIn &&
    plan.is_paid_plan !== false &&
    !plan.is_api
  );
}

export function shouldUseGetStartedLabel(
  plan: BillingPlan,
  context: PlanCtaContext,
) {
  return (
    usesDirectPurchaseOnlyVariant(plan, context) ||
    plan.is_api ||
    plan.is_free_plan ||
    plan.is_paid_plan === false ||
    Boolean(context.isLoggedIn && !context.isFreeTrialEligible)
  );
}

export function shouldUsePlanChangeCta(
  plan: BillingPlan,
  context: PlanCtaContext,
) {
  return Boolean(
    context.isLoggedIn &&
      context.hasPaidCurrentPlan &&
      !isCurrentPlan(plan) &&
      !isFreePlan(plan) &&
      plan.is_paid_plan !== false &&
      !plan.is_api,
  );
}

export function shouldUseCurrentPlanCta(
  plan: BillingPlan,
  context: PlanCtaContext,
) {
  const freeCurrentPlanFallback =
    !context.hasPaidCurrentPlan && isFreePlan(plan) && !plan.current_plan;

  return Boolean(
    context.isLoggedIn && (isCurrentPlan(plan) || freeCurrentPlanFallback),
  );
}
